import { useEffect, useState } from 'react'
import { api, type VendorDocument } from '../lib/api'
import type { EvidenceKind, LegalHold, OffboardingEvidence, RevokedAccess } from '../lib/exit'
import { checkOffboardingDate, todayIso, useLeftFields } from '../lib/dateRules'
import { DateRuleNote } from './DateRuleNote'
import { Picker } from './kit'

// The proof recorded with an exit step.
//
// Which kinds an item takes arrives from the API on the item itself, and every
// refusal names the field it is about, so this form offers the fields for the
// chosen kind and holds no rule about what is enough. What is enough is decided
// in packages/core/domain/offboarding_evidence.go, once.
//
// It sits inside a drawer on the exit workspace, so every label is a few words
// and nothing on it explains a rule: the refusal does that, naming the field.

const KIND_WORDS: Record<EvidenceKind, string> = {
  DOCUMENT: 'A filed document',
  REVOCATION: 'What was revoked',
  NONE_HELD: 'Nothing of this kind was held',
  NOTICE: 'The notice served',
  RETURN: 'Transition complete and what came back',
  DESTRUCTION: 'What was destroyed',
  RETENTION: 'Backups kept to a date',
  LEGAL_HOLD: 'Kept under a legal hold',
}

// What an item with no evidence rule of its own is offered: a filed document,
// or a statement that there was nothing to do. The other kinds are shaped for
// the items that ask for them, and offering a backup retention against
// returned laptops would be a form that fits nothing.
const OPTIONAL_KINDS: EvidenceKind[] = ['DOCUMENT', 'NONE_HELD']

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

// The evidence dates and their words on this form, which date rule D22's
// message starts with (the server uses the same words). Each records something
// that has happened, so none is in the future; the ones that are acts of the
// offboarding itself are also not before it began.
const DATE_LABEL: Record<string, string> = {
  servedOn: 'Served on',
  transitionCompletedOn: 'Transition completed on',
  receivedOn: 'Received on',
  deletedOn: 'Destroyed on',
  revokedOn: 'Revoked on',
}
const AFTER_START = new Set(['receivedOn', 'deletedOn', 'revokedOn'])

export function ExitEvidenceForm({
  vendorId,
  itemKey,
  accepts,
  value,
  onChange,
  disabled,
  holds,
  startedOn,
}: {
  vendorId: string
  itemKey: string
  accepts: EvidenceKind[]
  value: OffboardingEvidence | null
  onChange: (next: OffboardingEvidence | null) => void
  disabled: boolean
  // The holds standing on this vendor, when the caller has read them, so a
  // LEGAL_HOLD proof is chosen from the holds rather than typed as an id.
  holds?: LegalHold[]
  // The day the exit was opened, YYYY-MM-DD, where known (date rule D22).
  startedOn?: string
}) {
  const left = useLeftFields()
  const dateRule = (field: string, value: string) =>
    checkOffboardingDate(DATE_LABEL[field] ?? field, value, AFTER_START.has(field) ? (startedOn ?? '') : '', todayIso())
  const [documents, setDocuments] = useState<VendorDocument[] | null>(null)
  const kind = value?.kind

  useEffect(() => {
    if (kind !== 'DOCUMENT' || documents !== null) return
    let live = true
    api
      .vendorDocuments(vendorId)
      .then((r) => {
        if (live) setDocuments(r.documents)
      })
      .catch(() => {
        if (live) setDocuments([])
      })
    return () => {
      live = false
    }
  }, [kind, documents, vendorId])

  const base = 'ev-' + itemKey
  const set = (patch: Partial<OffboardingEvidence>) => value && onChange({ ...value, ...patch })

  function text(field: keyof OffboardingEvidence, label: string, type: 'text' | 'date' = 'text') {
    const id = base + '-' + String(field)
    const current = (value?.[field] as string | undefined) ?? ''
    const rule = type === 'date' && DATE_LABEL[String(field)] ? left.shown(String(field), dateRule(String(field), current)) : ''
    return (
      <div className="field" key={String(field)}>
        <label htmlFor={id}>{label}</label>
        <input
          id={id}
          type={type}
          disabled={disabled}
          value={current}
          onChange={(e) => set({ [field]: e.target.value } as Partial<OffboardingEvidence>)}
          onBlur={type === 'date' ? () => left.leave(String(field)) : undefined}
          aria-invalid={rule ? true : undefined}
          aria-describedby={rule ? id + '-rule' : undefined}
        />
        <DateRuleNote id={id + '-rule'} message={rule} />
      </div>
    )
  }

  const revoked: RevokedAccess[] = value?.revoked ?? []
  function setLine(i: number, patch: Partial<RevokedAccess>) {
    set({ revoked: revoked.map((r, j) => (j === i ? { ...r, ...patch } : r)) })
  }

  return (
    <fieldset className="fieldset" style={{ marginTop: 8 }}>
      <legend>Evidence</legend>
      <div className="field">
        <label htmlFor={base + '-kind'}>What proves it</label>
        <select
          id={base + '-kind'}
          disabled={disabled}
          value={kind ?? ''}
          onChange={(e) => {
            const k = e.target.value as EvidenceKind | ''
            if (k === '') return onChange(null)
            const next: OffboardingEvidence = { kind: k }
            if (k === 'REVOCATION') next.revoked = [{ system: '', identifier: '', revokedOn: today() }]
            if (k === 'RETURN') next.returned = true
            onChange(next)
          }}
        >
          <option value="">{accepts.length === 0 ? 'No evidence' : 'Choose'}</option>
          {(accepts.length > 0 ? accepts : OPTIONAL_KINDS).map((k) => (
            <option key={k} value={k}>
              {KIND_WORDS[k]}
            </option>
          ))}
        </select>
      </div>

      {kind === 'DOCUMENT' && (
        <div className="field">
          <label htmlFor={base + '-doc'}>Document</label>
          <Picker
            bare
            id={base + '-doc'}
            label="Document"
            disabled={disabled}
            value={value?.documentId ?? ''}
            options={
              documents === null
                ? null
                : documents
                    .filter((d) => d.contentSha256 && (d.source !== 'VENDOR' || d.acceptedAt))
                    .map((d) => ({ value: d.id, label: d.title + ' (' + d.docType + ')' }))
            }
            placeholder={documents === null ? 'Loading the documents' : 'Choose a filed document: search by title'}
            noun="document"
            missing="A document not in this list"
            onChange={(documentId) => set({ documentId })}
          />
          <p className="field-note">File it on the vendor's documents first</p>
        </div>
      )}

      {kind === 'REVOCATION' && (
        <>
          {revoked.map((r, i) => (
            <div className="form-row" key={i}>
              <div className="field">
                <label htmlFor={base + '-sys-' + i}>System</label>
                <input id={base + '-sys-' + i} type="text" disabled={disabled} value={r.system} onChange={(e) => setLine(i, { system: e.target.value })} />
              </div>
              <div className="field" style={{ flex: '1 1 auto' }}>
                <label htmlFor={base + '-id-' + i}>Account, key or certificate</label>
                <input id={base + '-id-' + i} type="text" disabled={disabled} value={r.identifier} onChange={(e) => setLine(i, { identifier: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor={base + '-on-' + i}>Revoked on</label>
                <input
                  id={base + '-on-' + i}
                  type="date"
                  disabled={disabled}
                  value={r.revokedOn}
                  onChange={(e) => setLine(i, { revokedOn: e.target.value })}
                  onBlur={() => left.leave('revokedOn-' + i)}
                  aria-invalid={left.shown('revokedOn-' + i, dateRule('revokedOn', r.revokedOn)) ? true : undefined}
                  aria-describedby={left.shown('revokedOn-' + i, dateRule('revokedOn', r.revokedOn)) ? base + '-on-' + i + '-rule' : undefined}
                />
                <DateRuleNote id={base + '-on-' + i + '-rule'} message={left.shown('revokedOn-' + i, dateRule('revokedOn', r.revokedOn))} />
              </div>
              <div className="field">
                <label htmlFor={base + '-by-' + i}>By</label>
                <input id={base + '-by-' + i} type="text" disabled={disabled} value={r.revokedBy ?? ''} onChange={(e) => setLine(i, { revokedBy: e.target.value })} />
              </div>
            </div>
          ))}
          <div className="chiprow">
            <button
              type="button"
              disabled={disabled}
              onClick={() => set({ revoked: [...revoked, { system: '', identifier: '', revokedOn: today() }] })}
            >
              Add a line
            </button>
            {revoked.length > 1 && (
              <button type="button" disabled={disabled} onClick={() => set({ revoked: revoked.slice(0, -1) })}>
                Remove the last line
              </button>
            )}
          </div>
        </>
      )}

      {kind === 'NONE_HELD' && (
        <>
          {text('statement', 'Why there was nothing, and how it was checked')}
          {text('confirmedBy', 'Confirmed by')}
        </>
      )}

      {kind === 'NOTICE' && (
        <div className="form-row">
          {text('servedOn', 'Served on', 'date')}
          {text('servedTo', 'Served to')}
          {text('method', 'How')}
        </div>
      )}

      {kind === 'RETURN' && (
        <>
          {text('transitionCompletedOn', 'Transition completed on', 'date')}
          <div className="check">
            <input
              id={base + '-returned'}
              type="checkbox"
              disabled={disabled}
              checked={value?.returned !== false}
              onChange={(e) => set({ returned: e.target.checked })}
            />
            <label htmlFor={base + '-returned'}>Our data came back</label>
          </div>
          {value?.returned !== false ? (
            <div className="form-row">
              {text('what', 'What came back, and in what form')}
              {text('receivedOn', 'Received on', 'date')}
            </div>
          ) : (
            text('statement', 'Why the data was not asked for back')
          )}
        </>
      )}

      {kind === 'DESTRUCTION' && (
        <div className="form-row">
          {text('what', 'What was destroyed')}
          {text('method', 'Method')}
          {text('deletedOn', 'Destroyed on', 'date')}
          {text('performedBy', 'Performed or confirmed by')}
        </div>
      )}

      {kind === 'RETENTION' && (
        <div className="form-row">
          {text('retainedUntil', 'Kept until', 'date')}
          {text('protection', 'What protects it until then')}
        </div>
      )}

      {kind === 'LEGAL_HOLD' &&
        (holds && holds.some((h) => h.standing) ? (
          <div className="field">
            <label htmlFor={base + '-hold'}>The legal hold</label>
            <select
              id={base + '-hold'}
              disabled={disabled}
              value={value?.legalHoldId ?? ''}
              onChange={(e) => set({ legalHoldId: e.target.value })}
            >
              <option value="">Choose a standing hold</option>
              {holds
                .filter((h) => h.standing)
                .map((h) => (
                  <option key={h.id} value={h.id}>
                    {(h.reference ? h.reference + ': ' : '') + h.reason}
                  </option>
                ))}
            </select>
          </div>
        ) : (
          text('legalHoldId', 'The legal hold id')
        ))}
    </fieldset>
  )
}
